/**
 * DC → Юнифанс → «Посещаемость» (21.09, владелец: «статистика в виде графиков за день
 * или выбранный период, по умолчанию неделя; гибкая и настраиваемая»).
 *
 * Период — чипами (День · Неделя · Месяц) или календарём шапки: чипы его сеют, чтобы
 * календарь и график показывали одно и то же. Метрика переключается мгновенно — сервер
 * отдал все разрезы периода сразу.
 *
 * Чего здесь намеренно нет: разреза посетителей по каналам и экранам. Один человек за
 * день бывает и в «QR игры», и в «без метки», и в пяти разделах — стопка таких «людей»
 * была бы выше столбца «Посетители» и врала бы. Складываются только открытия и действия,
 * а взаимоисключающий разрез посетителей ровно один — новые и вернувшиеся.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { useUnifansVisits } from "../../hooks/useUnifansVisits";
import { BarsChart, LineChart, NUM, SERIES_COLORS, fmtNum, type Point, type Serie } from "./dcVisitsCharts";
import { ChannelsBlock, FrequencyBlock, FunnelBlock, ScreensBlock, WhenBlock, box, cap } from "./dcVisitsBlocks";

type Metric = "visitors" | "opens" | "events" | "newVisitors";
const METRICS: { id: Metric; label: string; add: boolean }[] = [
  { id: "visitors", label: "Посетители", add: false }, { id: "opens", label: "Открытия", add: true },
  { id: "events", label: "Действия", add: true }, { id: "newVisitors", label: "Новые", add: false },
];
const iso = (d: Date) => d.toISOString().slice(0, 10);
const todayBiz = () => iso(new Date(Date.now() + 5 * 3600000));
const shift = (d: string, n: number) => iso(new Date(Date.parse(`${d}T00:00:00Z`) + n * 86400000));
const dm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
const chip = (on: boolean): CSSProperties => ({
  padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
  border: "1px solid " + (on ? "var(--role)" : "var(--surface-3)"),
  background: on ? "var(--role)" : "transparent", color: on ? "#06121a" : "var(--text-2)",
});

/** Плитка: число крупно, под ним — сравнение с прошлым таким же периодом В ЛЮДЯХ.
 *  Проценты при 25–40 посетителях в день врут («+75%» — это три человека), поэтому
 *  процент показываем только когда прошлый период достаточно большой. */
function Tile({ label, value, prev, note, mark }: { label: string; value: number; prev?: number; note?: string; mark?: string }): ReactElement {
  const d = prev == null ? null : value - prev;
  const pct = prev != null && prev >= 20 ? Math.round(((value - prev) / prev) * 100) : null;
  const tone = d == null || d === 0 ? "var(--text-3)" : d > 0 ? "var(--green)" : "var(--orange)";
  return (
    <div style={{ ...box, padding: "8px 10px" }}>
      <div style={cap}>{label}</div>
      <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: "var(--text-1)", lineHeight: 1.1 }}>{fmtNum(value)}</div>
      <div style={{ ...NUM, fontSize: 10, color: tone }}>
        {note ?? (d == null ? "" : prev === 0 ? "прошлый период пуст"
          : `${d > 0 ? "+" : ""}${fmtNum(d)} к ${fmtNum(prev!)}${pct != null ? ` · ${pct > 0 ? "+" : ""}${pct}%` : ""}`)}
      </div>
      {mark ? <div style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)" }}>{mark}</div> : null}
    </div>
  );
}

export function DcVisitsSection({ from, to, onSetRange }: {
  from?: string; to?: string; onSetRange?: (from: string, to: string) => void;
}): ReactElement {
  /* Дефолт — неделя: на первом открытии сеем календарь шапки, дальше главный он. */
  useEffect(() => { onSetRange?.(shift(todayBiz(), -6), todayBiz()); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const effFrom = from || shift(todayBiz(), -6);
  const effTo = to || todayBiz();
  const [staff, setStaff] = useState(false);
  const [metric, setMetric] = useState<Metric>("visitors");
  const [split, setSplit] = useState(false);       // новые и вернувшиеся
  const [asTable, setAsTable] = useState(false);
  const [hoverPt, setHoverPt] = useState<Point | null>(null);
  const { data, loading, error, reload } = useUnifansVisits(effFrom, effTo, staff);

  /* Режим считаем по ОТВЕТУ, а не по пропсам: пока идёт запрос нового периода, на экране
     ещё старые данные, и «День» с недельными цифрами выглядел бы как правда. */
  const oneDay = (data?.from ?? effFrom) === (data?.to ?? effTo);
  const stale = !!data && (data.from !== effFrom || data.to !== effTo);
  const canSplit = metric === "visitors";
  const useSplit = split && canSplit && !oneDay;
  /* По часам сервер знает посетителей, действия и открытия; «новых» по часам не бывает. */
  const dayMetric: Metric = oneDay && metric === "newVisitors" ? "visitors" : metric;

  /* Бакеты: один день — по часам; до двух недель — бары; до 35 дней — линия; дальше — недели. */
  const { points, series, mode } = useMemo((): { points: Point[]; series: Serie[]; mode: "bars" | "line" } => {
    if (!data) return { points: [], series: [], mode: "bars" };
    const label = METRICS.find((m) => m.id === metric)!.label;
    if (oneDay) {
      const val = (h: { events: number; visitors: number; opens: number }) =>
        dayMetric === "events" ? h.events : dayMetric === "opens" ? h.opens : h.visitors;
      return {
        points: data.hours.map((h) => ({ x: String(h.k), label: String(h.k).padStart(2, "0"), values: { v: val(h) } })),
        series: [{ key: "v", label: METRICS.find((m) => m.id === dayMetric)!.label, color: SERIES_COLORS[0]! }],
        mode: "bars" as const,
      };
    }
    if (useSplit) {
      return {
        points: data.buckets.map((b) => ({ x: b.d, label: dm(b.d), values: { returning: Math.max(0, b.visitors - b.newVisitors), newVisitors: b.newVisitors } })),
        series: [
          { key: "returning", label: "Вернувшиеся", color: SERIES_COLORS[0]! },
          { key: "newVisitors", label: "Новые", color: SERIES_COLORS[3]! },
        ],
        mode: "bars" as const,
      };
    }
    const daily: Point[] = data.buckets.map((b) => ({ x: b.d, label: dm(b.d), values: { v: b[metric] } }));
    const one = [{ key: "v", label, color: SERIES_COLORS[0]! }];
    if (data.days > 35) {
      /* Недельные бакеты: 92 дня — это 14 столбцов вместо муара из 92 полосок. */
      const weeks: Point[] = [];
      const sum = (ps: Point[]) => ps.reduce((s, p) => s + (p.values.v ?? 0), 0);
      /* Режем С КОНЦА: последняя неделя всегда полная и заканчивается на `to`, неполным
         оказывается самый старый столбец — иначе «падение» рисовалось бы на свежем крае. */
      for (let end = daily.length; end > 0; end -= 7) {
        const chunk = daily.slice(Math.max(0, end - 7), end);
        weeks.unshift({
          x: chunk[0]!.x, label: dm(chunk[0]!.x),
          /* людей не складываем — среднее за день недели; открытия и действия суммируем */
          values: { v: metric === "visitors" || metric === "newVisitors" ? Math.round(sum(chunk) / chunk.length) : sum(chunk) },
        });
      }
      return { points: weeks, series: one, mode: "bars" as const };
    }
    return { points: daily, series: one, mode: data.days > 14 ? ("line" as const) : ("bars" as const) };
  }, [data, metric, dayMetric, oneDay, useSplit]);

  const wrap: CSSProperties = { display: "grid", gap: 8, padding: 10, paddingBottom: "calc(12px + var(--safe-bottom))" };
  if (loading && !data) return <div style={wrap}><div style={{ ...box, color: "var(--text-2)", fontSize: 12 }}>Считаю посещаемость…</div></div>;
  if (error) {
    return <div style={wrap}><div style={{ ...box, color: "var(--red)", fontSize: 12 }}>{error}
      <button type="button" style={{ ...chip(false), marginLeft: 8 }} onClick={reload}>Повторить</button></div></div>;
  }
  if (!data) return <div style={wrap} />;
  const t = data.totals;
  const weekly = !oneDay && data.days > 35;

  return (
    <div style={wrap}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
        {([["День", 0], ["Неделя", 6], ["Месяц", 29]] as [string, number][]).map(([label, back]) => {
          const f = shift(todayBiz(), -back), tt = todayBiz();
          return <button key={label} type="button" style={chip(effFrom === f && effTo === tt)} onClick={() => onSetRange?.(f, tt)}>{label}</button>;
        })}
        <span style={{ ...cap, marginLeft: 4 }}>{dm(data.from)}–{dm(data.to)} · {data.days} дн.</span>
        {stale || loading ? <span style={{ ...cap, color: "var(--cyan)" }}>обновляю…</span> : null}
        {data.partial ? <span style={{ ...cap, color: "var(--orange)" }}>часть данных не посчиталась</span> : null}
        <button type="button" style={{ ...chip(staff), marginLeft: "auto" }} onClick={() => setStaff((v) => !v)}>
          {staff ? "С сотрудниками" : "Без сотрудников"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(118px, 1fr))", gap: 6 }}>
        <Tile label="Посетители" value={t.visitors} prev={data.prev.visitors}
          mark={data.to === data.today ? "сегодня ещё идёт — период неполный" : undefined} />
        <Tile label="Открытия" value={t.opens} prev={data.prev.opens} />
        <Tile label="Новые" value={t.newVisitors} note={`вернувшихся ${fmtNum(t.returning)}`} />
        <Tile label="Вернулись назавтра" value={data.retention.returned} note={`из ${fmtNum(data.retention.yesterday)} вчерашних`} />
      </div>

      <section style={box}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
          {METRICS.map((m) => {
            const off = oneDay && m.id === "newVisitors";   // по часам «новых» не существует
            return (
              <button key={m.id} type="button" style={{ ...chip(!off && dayMetric === m.id), opacity: off ? 0.45 : 1 }} disabled={off}
                title={off ? "Новых считаем по дням, а не по часам" : undefined} onClick={() => setMetric(m.id)}>{m.label}</button>
            );
          })}
          {!oneDay ? (
            <button type="button" style={{ ...chip(useSplit), opacity: canSplit ? 1 : 0.45 }} disabled={!canSplit}
              title={canSplit ? "Разделить на новых и вернувшихся" : "Разрез есть только у посетителей"}
              onClick={() => setSplit((v) => !v)}>Новые и вернувшиеся</button>
          ) : null}
        </div>
        {mode === "line" && series.length === 1
          ? <LineChart points={points} series={series[0]!} onPick={setHoverPt} />
          : <BarsChart points={points} series={series} onPick={setHoverPt} partialX={weekly ? undefined : data.today} />}
        {series.length > 1 ? (
          <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
            {series.map((s) => (
              <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "var(--text-2)" }}>
                <i style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} aria-hidden="true" />{s.label}
              </span>
            ))}
          </div>
        ) : null}
        <div style={{ ...NUM, fontSize: 11, color: "var(--text-2)", marginTop: 6, minHeight: 16 }}>
          {hoverPt
            ? <>{oneDay ? `${hoverPt.label}:00` : hoverPt.label} · {series.map((s) => `${s.label} ${fmtNum(hoverPt.values[s.key] ?? 0)}`).join(" · ")}</>
            : <span style={{ color: "var(--text-3)" }}>
              {oneDay ? "Часы выбранного дня." : weekly ? "По неделям: людей — среднее за день недели, открытия и действия — суммой." : "Столбцы — за день."}
              {(oneDay ? dayMetric === "visitors" : metric === "visitors" || metric === "newVisitors")
                ? ` Уникальных ${oneDay ? "за день" : "за период"} ${fmtNum(oneDay || metric === "visitors" ? t.visitors : t.newVisitors)} — складывать столбцы нельзя: один человек попадает в ${oneDay ? "каждый свой час" : "каждый свой день"}.`
                : ""}
            </span>}
        </div>
        <button type="button" style={{ ...chip(asTable), marginTop: 6 }} onClick={() => setAsTable((v) => !v)}>
          {asTable ? "Скрыть таблицу" : "Показать таблицей"}
        </button>
        {asTable ? (
          <div style={{ marginTop: 6, maxHeight: 220, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead><tr>
                <th style={{ ...cap, textAlign: "left", padding: "2px 4px" }}>{oneDay ? "Час" : "День"}</th>
                {series.map((s) => <th key={s.key} style={{ ...cap, textAlign: "right", padding: "2px 4px" }}>{s.label}</th>)}
              </tr></thead>
              <tbody>
                {points.map((p) => (
                  <tr key={p.x}>
                    <td style={{ ...NUM, color: "var(--text-2)", padding: "2px 4px" }}>{p.label}</td>
                    {series.map((s) => <td key={s.key} style={{ ...NUM, color: "var(--text-1)", textAlign: "right", padding: "2px 4px" }}>{fmtNum(p.values[s.key] ?? 0)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <WhenBlock data={data} />
      <FrequencyBlock data={data} />
      <ScreensBlock data={data} />
      <ChannelsBlock data={data} />
      <FunnelBlock data={data} />
    </div>
  );
}
